import { Logger, LoggerTransformer, waitForConsent } from "@identity/utils";
import { inject, injectWithTransform, singleton } from "tsyringe";
import { Consents } from "./types";
import {
  Action,
  CommunicationService,
  eventsRepository,
} from "../CommunicationService";
import { ConsentProviderFactory } from "./consent-provider-factory";
import { InstantConfigService } from "../InstantConfig";
import { Providers } from "./consentProviders/providers";
import { WINDOW } from "@identity/symbols";
import { AgeGateHandler } from "../AgeGateHandler";
import { consentResolver } from "./consent-resolver";
import {
  ConsentProviderInterface,
  ConsentStubProviderInterface,
  isConsentStubProvider,
} from "./consentProviders/consent-provider-interface";
import { FANDOM_CONSENT_UPDATE } from "@identity/custom_events";

@singleton()
export class OptInService {
  private consentProvider:
    | ConsentProviderInterface
    | ConsentStubProviderInterface
    | null = null;
  private consentUpdated = false;

  constructor(
    @inject(CommunicationService)
    private readonly communicationService: CommunicationService,
    @injectWithTransform(Logger, LoggerTransformer, "OptInService")
    private readonly logger: Logger,
    @inject(ConsentProviderFactory)
    private readonly consentProviderFactory: ConsentProviderFactory,
    @inject(AgeGateHandler)
    private readonly ageGateHandler: AgeGateHandler,
    @inject(WINDOW)
    private readonly window: Window,
  ) {}

  init(instantConfigService: InstantConfigService) {
    const preferredConsentProvider =
      instantConfigService.get<Providers>("icPreferredCMP");
    this.consentProviderFactory
      .get(preferredConsentProvider)
      .then((consentProvider) => {
        this.consentProvider = consentProvider;
        this.consentProvider.subscribe(async (consents: Consents) => {
          // Handle consents here so AgeGate modal can be shown if needed
          // It waits for the CONSENT_READY event to be emitted
          this.handleConsents(consents);

          if (this.ageGateHandler.applies(!consentResolver(consents))) {
            const ageGateConsents =
              await this.ageGateHandler.waitForAgeGateDataAndSetCookie();
            this.handleConsents(ageGateConsents);
          }
          // In the future, we can implement else branch with L48 here
        });
      });
  }

  onReady(): Promise<void> {
    return waitForConsent(this.window);
  }

  private handleConsents(consents: Consents): void {
    this.logger.info(
      "Sending consents: ",
      consents,
      "update?",
      this.consentUpdated,
    );
    // CONSENT_UPDATE event is being dispatched when user updated their
    // consents during the same page view.
    if (this.consentUpdated) {
      this.sendConsentsUpdateToAdEngine(consents);
    }

    // Always send CONSENT_READY event as some parts of Fandom system
    // are not aware of new CONSENT_UPDATE event yet.
    // Most likely the implementation is broken anyway and subsequent events
    // are being ignored, but better be safe than sorry.
    this.sendConsentsToAdEngine(consents);

    this.sendFandomCmpEvent("fandomConsentUpdate", consents);

    if (!this.consentUpdated) {
      this.consentUpdated = true;
    }
  }

  private sendConsentsToAdEngine(consents: Consents): void {
    this.sendFandomCmpEvent(FANDOM_CONSENT_UPDATE, consents);
    this.communicationService.emit(
      new Action(eventsRepository.CONSENT_READY, consents),
    );
  }

  private sendConsentsUpdateToAdEngine(consents: Consents): void {
    this.sendFandomCmpEvent(FANDOM_CONSENT_UPDATE, consents);
    this.communicationService.emit(
      new Action(eventsRepository.CONSENT_UPDATE, consents),
    );
  }

  private sendFandomCmpEvent(event: string, consents: Consents) {
    const optOut = !consentResolver(consents);
    const ageGateApplies = this.ageGateHandler.applies(optOut);
    let fandomCmp: typeof window.fandomCmp = {
      ready: true,
      blocking: ageGateApplies,
      coppa: this.ageGateHandler.resolveCoppaStatus(optOut),
      optOut,
      consentApi: undefined,
      consentString: undefined,
      consents: undefined,
      allowed: {
        ads: !ageGateApplies,
        tracking: true,
      },
    };
    if (isConsentStubProvider(this.consentProvider)) {
      fandomCmp = {
        ...fandomCmp,
        consentApi: this.consentProvider.getConsentApi(),
        consentString: this.consentProvider.getConsentString(),
        consents: this.consentProvider.getConsents(optOut),
      };
    }

    this.window.fandomCmp = fandomCmp;
    this.window.dispatchEvent(
      new CustomEvent(event, {
        detail: fandomCmp,
      }),
    );
  }
}
